// Reto de luz y movimiento (ADR 0004 §8, producto/05 §2.3): la pantalla muestra los colores del
// calendario del servidor en sus instantes mientras se graba el vídeo del reto; para cada evento
// se anota lo observado (luminancia y RGB medio) del fotograma correspondiente. El SDK no juzga
// si la luz «respondió»: eso lo decide el servidor.
import { sleep as realSleep, type CaptureClock } from './clock.js';
import type { SourceFrame } from './FrameRing.js';
import type { CaptureChallengeEvent, CaptureChallengeSchedule, CaptureVideo } from './types.js';
import type { VideoRecorder } from './VideoRecorder.js';

/** Superficie donde se pinta el reto (a pantalla completa dentro del componente). */
export interface ChallengeDisplay {
  begin(): void;
  /** Pinta `color` (#rrggbb) atenuado por `intensity` (0..1). */
  show(color: string, intensity: number): void;
  /** Avance de la guía «gira despacio alrededor del paquete» (0..1). */
  motion(progress: number, sensor: boolean): void;
  end(): void;
}

/** Fuente de giro (DeviceMotion): entrega grados/s; devuelve la función para darse de baja. */
export type MotionSource = (onRate: (degPerSec: number, dtMs: number) => void) => () => void;

export interface ChallengeRunnerOptions {
  schedule: CaptureChallengeSchedule;
  clock: CaptureClock;
  display: ChallengeDisplay;
  /** Suscripción a los fotogramas del stream (la de `FrameRing.onFrame`). */
  frames: (listener: (f: SourceFrame) => void) => () => void;
  /** Grabador del vídeo del reto (kind `challenge`), aún sin arrancar. */
  recorder: VideoRecorder;
  /** Duración objetivo del calendario (ms); si se da, se escala (p. ej. `fastChallenge` ≈ 1500). */
  targetMs?: number;
  /** Grabación mínima (ms) para tener ≥ 3 trozos de 1 s. Por defecto 3200. */
  minRecordMs?: number;
  motion?: MotionSource | null;
  sleep?: (ms: number) => Promise<void>;
}

export interface ChallengeOutcome {
  events: CaptureChallengeEvent[];
  video: CaptureVideo | null;
}

/** Grados de giro con los que la guía de movimiento se da por completada. */
const ORBIT_DEGREES = 90;

/** `DeviceMotion` del navegador, si existe (pide permiso en iOS cuando hace falta). */
export async function deviceMotionSource(): Promise<MotionSource | null> {
  const DME = (globalThis as { DeviceMotionEvent?: { requestPermission?: () => Promise<string> } })
    .DeviceMotionEvent;
  if (!DME || typeof addEventListener !== 'function') return null;
  try {
    if (
      typeof DME.requestPermission === 'function' &&
      (await DME.requestPermission()) !== 'granted'
    )
      return null;
  } catch {
    return null;
  }
  return (onRate) => {
    const handler = (ev: DeviceMotionEvent) => {
      const r = ev.rotationRate;
      if (!r) return;
      onRate(Math.hypot(r.alpha ?? 0, r.beta ?? 0, r.gamma ?? 0), ev.interval || 16);
    };
    addEventListener('devicemotion', handler);
    return () => removeEventListener('devicemotion', handler);
  };
}

/**
 * Ejecuta el reto.
 *
 * ```ts
 * const out = await new ChallengeRunner({ schedule, clock, display, frames, recorder }).run();
 * out.events; // [{ t_mono, expected: { color, intensity }, observed: { luma, mean_rgb } }]
 * ```
 */
export class ChallengeRunner {
  constructor(private readonly opts: ChallengeRunnerOptions) {}

  /** Duración natural del calendario (ms), sin el `lead_ms`. */
  static span(schedule: CaptureChallengeSchedule): number {
    return Math.max(0, ...schedule.events.map((e) => e.t_offset_ms + e.duration_ms));
  }

  async run(): Promise<ChallengeOutcome> {
    const { schedule, clock, display, recorder } = this.opts;
    const sleep = this.opts.sleep ?? realSleep;
    const span = ChallengeRunner.span(schedule);
    const scale = this.opts.targetMs && span > 0 ? Math.min(1, this.opts.targetMs / span) : 1;
    const lead = schedule.lead_ms * scale;
    const motionMs = Math.max(span, schedule.motion.duration_ms) * scale;
    const minRecordMs = this.opts.minRecordMs ?? 3200;

    const seen: SourceFrame[] = [];
    const offFrames = this.opts.frames((f) => seen.push(f));
    let degrees = 0;
    const offMotion = this.opts.motion?.((rate, dt) => (degrees += (rate * dt) / 1000)) ?? null;

    const startedAt = clock.mono();
    const at = async (ms: number) => {
      const wait = ms - (clock.mono() - startedAt) / 1000;
      if (wait > 0) await sleep(wait);
    };
    const progress = () => {
      const elapsed = (clock.mono() - startedAt) / 1000 - lead;
      const byTime = Math.min(1, Math.max(0, elapsed / Math.max(1, motionMs)));
      display.motion(
        offMotion ? Math.max(byTime, Math.min(1, degrees / ORBIT_DEGREES)) : byTime,
        !!offMotion,
      );
    };

    display.begin();
    recorder.start();
    const targets: number[] = [];
    try {
      display.show('#000000', 0);
      await at(lead);
      const t0 = clock.mono();
      for (const e of schedule.events) {
        await at(lead + e.t_offset_ms * scale);
        display.show(e.color, e.intensity);
        progress();
        // Se observa a mitad del evento: la cámara tarda en reflejar el cambio de luz.
        targets.push(t0 + Math.round((e.t_offset_ms + e.duration_ms / 2) * scale * 1000));
        await at(lead + (e.t_offset_ms + e.duration_ms) * scale);
        display.show('#000000', 0);
      }
      await at(lead + motionMs);
      progress();
      await at(minRecordMs);
      // Un fotograma más tras el último evento para que el más cercano exista seguro.
      await sleep(50);
    } finally {
      offFrames();
      offMotion?.();
      display.end();
    }
    const video = await recorder.stop();

    const events = schedule.events.map((e, k): CaptureChallengeEvent => {
      const target = targets[k]!;
      const frame = nearest(seen, target);
      const t_mono = target - Math.round((e.duration_ms / 2) * scale * 1000);
      return {
        t_mono: Math.max(0, t_mono),
        expected: { color: e.color, intensity: e.intensity },
        observed: frame
          ? { luma: frame.luma, mean_rgb: [frame.meanRgb[0], frame.meanRgb[1], frame.meanRgb[2]] }
          : { luma: 0, mean_rgb: [0, 0, 0] },
      };
    });
    return { events, video };
  }
}

function nearest(frames: readonly SourceFrame[], t: number): SourceFrame | undefined {
  let best: SourceFrame | undefined;
  for (const f of frames) if (!best || Math.abs(f.t_mono - t) < Math.abs(best.t_mono - t)) best = f;
  return best;
}
